import React, { useMemo } from 'react';
import type DecisionType from '@modules/product/type/decision-type';
import type Attribute from '@modules/product/type/attribute';
import type AttributeOption from '@modules/product/type/attribute-option';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useLandingVariant } from '../hook/use-landing-variant';

interface AttributeWithOptionsType extends Attribute {
    options: AttributeOption[];
    isGeneric: boolean;
}

interface VariantOptionProviderProps {
    product: ProductPageLoaderData['product'];
    children: React.ReactNode | React.ReactNode[];
}

interface VariantOptionState {
    decision: DecisionType;
    setDecision(d: DecisionType): void;
    attributeMap: Map<number, AttributeWithOptionsType>;
    attributeOptionMap: Map<number, Attribute>;
}

const VariantOptionContext = React.createContext<
    VariantOptionState | undefined
>(undefined);

const useVariantOptionState = (): VariantOptionState => {
    const context = React.useContext(VariantOptionContext);
    if (!context)
        throw new Error('Cannot be used outside VariantOptionContext');
    return context;
};

const VariantOptionProvider = ({
    product,
    children,
}: VariantOptionProviderProps) => {
    const landingVariant = useLandingVariant(product);

    const optionMaps = React.useMemo(() => {
        const attributeOptions = product.AttributeOptions ?? [];
        const attributeMap = attributeOptions.reduce((map, opt) => {
            // Skip orphaned options whose Attribute no longer exists.
            if (!opt.Attribute) return map;
            const attr =
                map.get(opt.Attribute.ID) ||
                ({
                    ...opt.Attribute,
                    options: [],
                    isGeneric: false,
                } as AttributeWithOptionsType);
            attr.options.push(opt);
            return map.set(attr.ID, attr);
        }, new Map<number, AttributeWithOptionsType>());

        const attributeOptionMap = attributeOptions.reduce(
            (map, opt) =>
                opt.Attribute ? map.set(opt.ID, opt.Attribute) : map,
            new Map<number, Attribute>(),
        );

        for (const [key, value] of attributeMap) {
            value.isGeneric = product.Variants.every((v) =>
                v.Options.some((opt) => opt.AttributeID === key),
            );
        }

        return {
            attributeMap,
            attributeOptionMap,
        };
    }, [product]);

    const [decision, setDecision] = React.useState<DecisionType>({
        options: landingVariant
            ? landingVariant.Options.reduce(
                  (decision, opt) => {
                      const attribute = optionMaps.attributeOptionMap.get(
                          opt.ID,
                      );
                      if (attribute) decision[attribute.ID] = opt.ID;
                      return decision;
                  },
                  {} as DecisionType['options'],
              )
            : {},
        size: landingVariant?.Size?.ID ?? null,
    });

    const value = useMemo(
        () => ({
            decision,
            setDecision,
            ...optionMaps,
        }),
        [optionMaps, decision, setDecision],
    );

    return (
        <VariantOptionContext.Provider value={value}>
            {children}
        </VariantOptionContext.Provider>
    );
};

export { VariantOptionProvider, useVariantOptionState };
